import React from "react";
import Badge from "@/components/ui/badge";
import { getEntityTypeById } from "@/entities/tasks/helpers";
import {
  EntityMapping,
  TaskEntity,
  TaskFormData
} from "@/entities/tasks/types";
import {
  EntityType,
  entityTypeToFieldName
} from "@/services/hooks/entities/useEntityType";
import { Account, Diamond, Magnet, User } from "@dreamhub/designsystem/icons";
import EntityBadge from "@/components/layouts/TasksFocus/EntityBadge";
import ClickableTooltip from "@/components/controls/ClickableTooltip";
import { map } from "lodash";

const ENTITY_MAPPING: EntityMapping = {
  [EntityType.DEAL]: {
    prefix: "d-",
    field: entityTypeToFieldName[EntityType.DEAL] as keyof TaskFormData,
    icon: <Diamond />
  },
  [EntityType.LEAD]: {
    prefix: "l-",
    field: entityTypeToFieldName[EntityType.LEAD] as keyof TaskFormData,
    icon: <Magnet />
  },
  [EntityType.COMPANY]: {
    prefix: "c-",
    field: entityTypeToFieldName[EntityType.COMPANY] as keyof TaskFormData,
    icon: <Account />
  },
  [EntityType.PERSON]: {
    prefix: "p-",
    field: entityTypeToFieldName[EntityType.PERSON] as keyof TaskFormData,
    icon: <User />
  }
};

interface Props {
  entities: TaskEntity[];
}

const EntitiesCell: React.FC<Props> = ({ entities }) => {
  const [first, second, ...rest] = entities;
  const firstTwo = [first, second].filter(Boolean);
  const remaining = rest;

  const mapEntities = (entities: TaskEntity[]) => {
    return map(entities, (entity) => {
      const entityType = getEntityTypeById(entity.entityId, ENTITY_MAPPING);
      if (entityType === undefined) return null;
      const mapping = ENTITY_MAPPING[entityType as EntityType];

      return (
        <EntityBadge
          key={entity.entityId}
          {...entity}
          entityType={entityType}
          icon={mapping?.icon}
        />
      );
    });
  };

  return (
    <div className="flex gap-1 items-center">
      {mapEntities(firstTwo)}
      {remaining?.length > 0 && (
        <ClickableTooltip
          trigger={
            <Badge
              text={`+${remaining?.length}`}
              textVariant="smBodySecondary"
              className="p-1 transition-colors duration-200 hover:bg-white-8"
            />
          }
          content={
            <div className="flex flex-col gap-1">{mapEntities(remaining)}</div>
          }
        />
      )}
    </div>
  );
};

export default EntitiesCell;
